import { useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { formatCardDate } from "@/design/format";
import { cn } from "@/design/cn";
import { CalendarRange, Sparkles, History } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { ToolbarSelect, Button, EmptyState, NarrativeCard, Stat, MilestoneStatusPill, SpendBudgetStat } from "@/design/components";
import { useSeed, useVisibleProjects, useMoney, useActorId, useMutate, useActiveProfile } from "@/domain/hooks";
import { importProcurementPlanDraft } from "@/domain/mutations";
import { useUpdateMilestone } from "@/domain/useMutations";
import type { Milestone } from "@/domain/types";
import { toast } from "sonner";

const MS = 86_400_000;

export function PlanningSurface() {
  const { workspaceId } = useParams();
  const seed = useSeed();
  const projects = useVisibleProjects();
  const money = useMoney();
  const actorId = useActorId();
  const mutate = useMutate();
  const { update: updateMilestone } = useUpdateMilestone();
  const profile = useActiveProfile();
  const [searchParams] = useSearchParams();
  const canEdit = profile.group === "contractor" || profile.group === "oversight";
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [importing, setImporting] = useState(false);

  // Project health left Planning for the Projects surface. Honour any old ?tab=health
  // deep-link by sending it there, so Planning is purely plan / milestones now.
  if (searchParams.get("tab") === "health") {
    return <Navigate to={`/workspace/${workspaceId}/projects`} replace />;
  }

  const project = projects.find((p) => p.id === projectId);
  const milestones = seed.milestones
    .filter((m) => m.projectId === projectId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.targetDate < b.targetDate ? -1 : 1));

  if (!project) {
    return (
      <Wrap>
        <EmptyState title="No project to plan" body="Projects you lead or oversee appear here for planning. Add a project to begin." />
      </Wrap>
    );
  }

  // Derive planned vs actual per milestone.
  const rows = milestones.map((m, i) => {
    const plannedBudget = m.plannedBudget ?? m.budgetSharePct * project.budgeted;
    const actualSpent = seed.spendRequests
      .filter((r) => r.milestoneId === m.id && (r.state === "committed" || r.state === "evidence_submitted" || r.state === "confirmed"))
      .reduce((a, r) => a + r.amount, 0);
    const plannedStart = i === 0 ? seed.programme.startDate : milestones[i - 1].targetDate;
    const plannedEnd = m.targetDate;
    const disb = seed.disbursements.find((d) => d.milestoneId === m.id && d.approvedAt);
    const projected = seed.analytics.milestones.find((x) => x.name === m.name)?.projectedDate;
    const actualEnd = disb?.approvedAt ?? projected ?? m.targetDate;
    return { m, plannedBudget, actualSpent, plannedStart, plannedEnd, actualEnd };
  });

  // Timeline bounds.
  const allDates = rows.flatMap((r) => [r.plannedStart, r.plannedEnd, r.actualEnd]).map((d) => new Date(d).getTime());
  const minT = Math.min(...allDates, new Date(seed.programme.startDate).getTime());
  const maxT = Math.max(...allDates);
  const span = Math.max(1, maxT - minT);
  const pct = (d: string) => ((new Date(d).getTime() - minT) / span) * 100;
  const todayPct = ((Date.now() - minT) / span) * 100;
  const todayCol = (p: number) => Math.min(100, Math.max(0, p));

  const totalPlanned = rows.reduce((a, r) => a + r.plannedBudget, 0);
  const totalActual = rows.reduce((a, r) => a + r.actualSpent, 0);
  const driftDays = project.scheduleDriftDays ?? 0;
  const revisions = milestones.reduce((a, m) => a + (m.planRevisions ?? 0), 0);

  function doImport() {
    setImporting(true);
    // Staged moment, then a pre-filled draft for review (mocked AI onboarding).
    window.setTimeout(() => {
      mutate((s) => importProcurementPlanDraft(s, projectId, actorId));
      setImporting(false);
      toast.success("Procurement plan imported as a draft. Review and confirm the milestones.");
    }, 1600);
  }

  return (
    <Wrap>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-faint">Project</span>
            <ToolbarSelect value={projectId} onChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} />
          </label>
        </div>
        {canEdit && (
          <Button variant="secondary" onClick={doImport} disabled={importing}>
            <Sparkles className="h-4 w-4" /> {importing ? "Importing…" : "Import procurement plan"}
          </Button>
        )}
      </div>

      {/* Plan-vs-actual summary. Planned budget and actual spend are one combined card —
          burn against plan stays brand-tone, never RAG-coloured (schedule carries the RAG). */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Stat label="Milestones" value={String(milestones.length)} />
        <div className="flex flex-col justify-center rounded-card border border-line bg-card p-4">
          <span className="text-meta text-faint">Spend against plan</span>
          <SpendBudgetStat
            className="mt-3"
            label="Spend against plan"
            spent={totalActual}
            budget={totalPlanned}
            spentLabel={money(totalActual)}
            budgetLabel={money(totalPlanned)}
          />
        </div>
        <Stat
          label="Schedule"
          value={driftDays > 0 ? `${driftDays}d` : driftDays < 0 ? `${-driftDays}d` : "On plan"}
          sub={driftDays > 0 ? "behind plan" : driftDays < 0 ? "ahead of plan" : "on plan"}
          subTone={driftDays > 0 ? "warn" : "ok"}
        />
      </div>

      {/* Timeline visual */}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-faint">
          <CalendarRange className="h-3.5 w-3.5" /> Delivery timeline · planned vs actual
        </h3>
        <div className="space-y-2 rounded-lg border border-line bg-card p-4">
          <div className="space-y-2 pt-3">
            {rows.map((r) => {
              const pStart = pct(r.plannedStart);
              const pEnd = pct(r.plannedEnd);
              const aEnd = pct(r.actualEnd);
              const complete = r.m.status === "disbursed" || r.m.status === "closed";
              const late = new Date(r.actualEnd).getTime() > new Date(r.plannedEnd).getTime() + MS;
              // actual overlay tone: complete -> ok, behind -> warn, otherwise on pace -> info.
              const actualFill = complete ? "bg-okFill" : late ? "bg-warnFill" : "bg-infoFill";
              return (
                <div key={r.m.id} className="grid grid-cols-12 items-center gap-2">
                  <span className="col-span-4 truncate text-xs text-ink md:col-span-3">{r.m.name}</span>
                  <div className="relative col-span-8 h-5 md:col-span-9">
                    {/* today line, aligned to the bar track */}
                    <div className="pointer-events-none absolute inset-y-0 z-10 w-px bg-strong" style={{ left: `${todayCol(todayPct)}%` }} />
                    {/* planned range: outline track, transparent fill */}
                    <div
                      className="absolute top-1 h-3 rounded-full border border-strong bg-transparent"
                      style={{ left: `${pStart}%`, width: `${Math.max(1, pEnd - pStart)}%` }}
                    />
                    {/* actual / projected: solid overlay coloured by pace */}
                    <div
                      className={`absolute top-1.5 h-2 rounded-full ${actualFill}`}
                      style={{ left: `${pStart}%`, width: `${Math.max(1, aEnd - pStart)}%` }}
                      title={`Planned ${format(new Date(r.plannedEnd), "d MMM")} · projected ${format(new Date(r.actualEnd), "d MMM")}`}
                    />
                  </div>
                </div>
              );
            })}
            {/* today date tag in the data voice, aligned under the bar track */}
            <div className="grid grid-cols-12 gap-2">
              <span className="col-span-4 md:col-span-3" />
              <div className="relative col-span-8 h-4 md:col-span-9">
                <span
                  className="data absolute top-0 -translate-x-1/2 whitespace-nowrap rounded-control bg-subtle px-1 text-[10px] text-mute"
                  style={{ left: `${todayCol(todayPct)}%` }}
                >
                  today · {format(new Date(), "d MMM yyyy")}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 border-t border-hairline pt-2 text-[11px] text-faint">
            <span className="flex items-center gap-1"><span className="h-2 w-4 rounded-full border border-strong bg-transparent" /> Planned</span>
            <span className="flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-infoFill" /> On pace</span>
            <span className="flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-warnFill" /> Behind</span>
            <span className="flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-okFill" /> Complete</span>
          </div>
        </div>
      </section>

      {/* Editable plan + plan-vs-actual table */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Milestone plan</h3>
          {revisions > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-faint"><History className="h-3 w-3" /> {revisions} revision{revisions === 1 ? "" : "s"} since approval</span>
          )}
        </div>
        {/* overflow-hidden on the outer div is only for the rounded
            corners - this is a 6-column table with per-row inputs
            including fixed-width fields (w-28, w-48 below), the same
            "clips instead of scrolls" gap DataTable already solves
            elsewhere; the inner overflow-x-auto + table min-width restores
            that escape hatch here. */}
        <div className="overflow-hidden rounded-lg border border-line bg-card">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-xs text-faint">
                <th className="px-3 py-2 font-medium">Milestone</th>
                <th className="px-3 py-2 font-medium">Target date</th>
                <th className="px-3 py-2 text-right font-medium">Planned budget</th>
                <th className="px-3 py-2 text-right font-medium">Actual spend</th>
                <th className="px-3 py-2 font-medium">Evidence required</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <PlanRow
                  key={r.m.id}
                  m={r.m}
                  plannedBudget={r.plannedBudget}
                  actualSpent={r.actualSpent}
                  money={money}
                  canEdit={canEdit}
                  onCommit={(patch) => { updateMilestone(r.m.id, patch); }}
                />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </section>

      <NarrativeCard
        variant="interpretation"
        title="Plan to actual"
        body={`This project carries ${money(totalPlanned)} of planned milestone budget; ${money(totalActual)} has been spent against it so far. The plan is the spine the field spend lands against, and the same view feeds the pace forecast. Revising a date or budget after approval is recorded in the audit trail.`}
      />
    </Wrap>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <WorkspaceShell>
      <div className="space-y-6 px-4 py-5 md:px-8 md:py-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Planning</h1>
          <p className="mt-1 text-sm text-mute">Build the delivery plan before spend starts, then track planned versus actual as the work proceeds.</p>
        </div>
        {children}
      </div>
    </WorkspaceShell>
  );
}

function PlanRow({
  m,
  plannedBudget,
  actualSpent,
  money,
  canEdit,
  onCommit,
}: {
  m: Milestone;
  plannedBudget: number;
  actualSpent: number;
  money: (n: number) => string;
  canEdit: boolean;
  onCommit: (patch: { targetDate?: string; plannedBudget?: number; evidenceRequirements?: string[] }) => void;
}) {
  const [date, setDate] = useState(m.targetDate.slice(0, 10));
  const [budgetM, setBudgetM] = useState(String(Math.round(plannedBudget / 1_000_000)));
  const [reqs, setReqs] = useState((m.evidenceRequirements ?? []).join(", "));
  const over = actualSpent > plannedBudget;

  return (
    <tr className={cn("border-b border-hairline last:border-0", over && "bg-warnSoft")}>
      <td className="px-3 py-2 font-medium text-ink">{m.name}</td>
      <td className="px-3 py-2">
        {canEdit ? (
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onBlur={() => date && date !== m.targetDate.slice(0, 10) && onCommit({ targetDate: date })}
            className="rounded-md border border-line bg-card px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand"
          />
        ) : (
          <span className="text-mute">{formatCardDate(m.targetDate)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {canEdit ? (
          <span className="inline-flex w-28 items-baseline justify-end gap-1 rounded-control border border-line bg-card px-2 py-1 focus-within:ring-2 focus-within:ring-brand">
            <input
              value={budgetM}
              onChange={(e) => setBudgetM(e.target.value.replace(/[^0-9]/g, ""))}
              onBlur={() => {
                const next = Number(budgetM) * 1_000_000;
                if (next && next !== plannedBudget) onCommit({ plannedBudget: next });
              }}
              className="w-12 bg-transparent text-right text-xs tabular-nums text-ink focus:outline-none"
            />
            <span className="data whitespace-nowrap text-[11px] text-faint">M UGX</span>
          </span>
        ) : (
          <span className="tabular-nums text-mute">{money(plannedBudget)}</span>
        )}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums ${over ? "text-warn" : "text-ink"}`}>{money(actualSpent)}</td>
      <td className="px-3 py-2">
        {canEdit ? (
          <input
            value={reqs}
            onChange={(e) => setReqs(e.target.value)}
            onBlur={() => onCommit({ evidenceRequirements: reqs.split(",").map((x) => x.trim()).filter(Boolean) })}
            placeholder="Comma-separated"
            className="w-48 rounded-md border border-line bg-card px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand"
          />
        ) : (
          <span className="text-xs text-mute">{(m.evidenceRequirements ?? []).join(", ") || "—"}</span>
        )}
      </td>
      <td className="px-3 py-2"><MilestoneStatusPill status={m.status} /></td>
    </tr>
  );
}
